import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Eye, EyeOff, Check, X } from 'lucide-react';

interface Requirement {
  key: string;
  label: string;
  met: boolean;
}

interface PasswordFieldProps {
  id: string;
  label?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: string;
  showRequirements?: boolean;
  requirements?: Requirement[];
  className?: string;
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder = 'Enter password',
  error,
  showRequirements = false,
  requirements = [],
  className,
}) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);

  return (
    <div data-testid={`password-field-${id}`} className={cn('space-y-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
        >
          {label}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          data-testid={`password-input-${id}`}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={cn(
            'w-full h-11 px-4 pr-11 rounded-lg',
            'bg-white dark:bg-slate-800',
            'text-slate-900 dark:text-slate-100',
            'placeholder:text-slate-400 dark:placeholder:text-slate-500',
            'transition-colors duration-150',
            error
              ? 'border-2 border-rose-500 focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20'
              : 'border border-slate-200 dark:border-slate-700 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20',
            'focus:outline-none'
          )}
        />
        <button
          type="button"
          data-testid={`password-toggle-${id}`}
          onClick={() => setShowPassword(!showPassword)}
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2',
            'p-1 rounded-md',
            'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300',
            'hover:bg-slate-100 dark:hover:bg-slate-700',
            'transition-colors duration-150'
          )}
        >
          {showPassword ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <p
          data-testid={`password-error-${id}`}
          className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-1.5"
        >
          <X className="w-3.5 h-3.5" />
          {error}
        </p>
      )}

      {/* Password Requirements */}
      {showRequirements && requirements.length > 0 && (
        <div
          data-testid="password-requirements"
          className="mt-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800"
        >
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
            Password requirements:
          </p>
          <ul className="space-y-1.5">
            {requirements.map((req) => (
              <li
                key={req.key}
                data-testid={`requirement-${req.key}`}
                className={cn(
                  'flex items-center gap-2 text-xs',
                  req.met
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-slate-500 dark:text-slate-400'
                )}
              >
                {req.met ? (
                  <Check className="w-3.5 h-3.5" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border border-slate-300 dark:border-slate-600" />
                )}
                {req.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
